import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { opportunityPaths } from "../../data/content.js";
import { RevealItem, Rise, mailto } from "./shared.jsx";
import "./join.css";

const LISTING = [
  ["The work", "What you would make or do, and the output at the end"],
  ["Named supervisor", "The person who owns the work and reviews it"],
  ["Duration", "Start and end"],
  ["Weekly hours", "A realistic number"],
  ["Location", "Online, in person or both"],
  ["Eligibility", "Who can apply"],
  ["Compensation", "Paid, unpaid or other, stated plainly"],
  ["Application process", "How to apply and what happens next"],
];

function ScopeTimeline({ steps }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.45"] });
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const fill = useTransform(scrollYProgress, (v) => (wide ? `scaleX(${clamp(v)})` : `scaleY(${clamp(v)})`));
  const marker = useTransform(scrollYProgress, (v) => (wide ? `translateX(${clamp(v) * 100}%)` : `translateY(${clamp(v) * 100}%)`));
  const animated = !reduce;

  return (
    <div className="jn-in-line" ref={ref}>
      <div className="jn-in-line__track" aria-hidden="true">
        <motion.span className="jn-in-line__fill" style={animated ? { transform: fill } : undefined} />
        <motion.span className="jn-in-line__runner" style={animated ? { transform: marker } : undefined}>
          <span className="jn-in-line__dot" />
        </motion.span>
      </div>
      <ol>
        {steps.map((step, index) => (
          <li key={step}>
            <span className="jn-mono">Stage {index + 1}</span>
            <strong>{step}</strong>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function InternshipsPage() {
  const selected = opportunityPaths.find((item) => item.slug === "internships");
  const email = mailto(selected.emailSubject);

  return (
    <PageFrame className="rd-page jn-page jn-in">
      <Seo title={`${selected.title} | Project GRID`} path={`/student-opportunities/${selected.slug}`} description={selected.body} />
      <main>
        <header className="jn-wrap jn-in-hero">
          <Rise className="jn-in-status" delay={0.05} role="note">
            <span className="jn-in-status__label jn-mono">Current status</span>
            <span>There is no confirmed internship listing right now. You can ask whether a suitable role is available.</span>
          </Rise>
          <MaskWords as="h1" className="jn-h1 jn-in-hero__title" text="Learn through work that has an owner." onMount delay={0.15} />
          <div className="jn-in-hero__row">
            <Rise as="p" className="jn-lede" delay={0.45}>
              {selected.promise} {selected.body}
            </Rise>
            <Rise className="jn-in-hero__photo" delay={0.55}>
              <Photo src={selected.image} alt="A smiling participant in the AAROH auditorium at IIIT Delhi" sizes="(max-width: 899px) 100vw, 30vw" priority />
            </Rise>
          </div>
        </header>

        <section className="jn-wrap jn-in-scope" aria-labelledby="jn-in-scope-title">
          <header>
            <MaskWords id="jn-in-scope-title" className="jn-h2" text="Scope the contribution before the title." />
            <p className="jn-lede">An internship at GRID would be a bounded project with a clear output, an owner and a review point. It moves through four stages.</p>
          </header>
          <ScopeTimeline steps={selected.steps} />
          <div className="jn-in-fits">
            <p className="jn-mono">Possible scopes</p>
            <ul>
              {selected.fits.map((fit) => <li key={fit}>{fit}</li>)}
            </ul>
          </div>
        </section>

        <section className="jn-wrap jn-in-listing" aria-labelledby="jn-in-listing-title">
          <div className="jn-in-listing__head">
            <MaskWords id="jn-in-listing-title" className="jn-h2" text="What a listing must explain." />
            <p className="jn-lede">Before applications open, every one of these should be clear. If a role you are offered is missing any of them, ask.</p>
          </div>
          <dl className="jn-in-listing__sheet">
            {LISTING.map(([term, detail], index) => (
              <RevealItem as="div" key={term} delay={index * 0.04}>
                <dt>{term}</dt>
                <dd>{detail}</dd>
              </RevealItem>
            ))}
          </dl>
        </section>

        <section className="jn-wrap jn-in-ask" aria-labelledby="jn-in-ask-title">
          <h2 id="jn-in-ask-title" className="jn-h2">Ask about internships.</h2>
          <p>Share your learning goal and the kind of bounded project you would like to work on. The email opens with the subject "{selected.emailSubject}".</p>
          <div className="gh-actions">
            <a className="gh-btn gh-btn--fill" href={email}>Email an internship enquiry <ArrowUpRight weight="bold" aria-hidden="true" /></a>
            <Link className="gh-link" to="/team">{selected.cta} <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
